import { Injectable, Type } from "@angular/core";
import { CubiqWorker } from "../../worker/CubiqWorker";
import { Subject } from "rxjs";
import { IRateSubscriptionService, CHANNEL_RATE_SUBSCRIPTION } from "../IRateSubscriptionService";
import { IRateSubscriptionSource } from "../IRateSubscriptionSource";
import { RateSubscription } from "../model/RateSubscription";
import { RateSubscriptionEvent, RateSubscriptionEventType } from "../model/RateSubscriptionEvent";
import { OrderDataPromiseLookupService } from "../../lookup/model/impl/OrderDataPromiseLookupService";
import { OrderDataLookupService } from "../../lookup/model/impl/OrderDataLookupService";
import {
  PRIMITIVE,
  ServiceMessageBrokerFactory,
  ServiceMessageBroker,
} from "shared/provider-webworker";
import { ENCODABLE_TYPE_ERRORS } from "shared/broker-client-services";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { CURVE_SPT } from "./RateCache";
import { CURVE_FWD } from "./RateCache";
import { CURVE_NDF } from "./RateCache";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { Rate } from "../model/Rate";

@Injectable()
export class RateSubscriptionPrimaryService implements IRateSubscriptionService {
  private subscriptions: any = {};
  private pendingSubscriptions: any = {};
  private pendingUnsubscribe: any = {};
  protected broker: ServiceMessageBroker;
  private rateSubscriptionSubject: Subject<RateSubscriptionEvent> =
    new Subject<RateSubscriptionEvent>();
  protected readonly VALUE: CubiqTS.ConcreteEncodable;

  constructor(
    private worker: CubiqWorker,
    private orderService: OrderDataPromiseLookupService,
    protected factory: ServiceMessageBrokerFactory,
  ) {
    //  protected modelMarketDataType: Type<CubiqTS.ConcreteEncodable> = CubiqTS.MarketDataEventImpl
    this.VALUE = CubiqTS.ENCODABLE(CubiqTS.MarketDataEventImpl);
    this.initializeChannels();
  }

  protected initializeChannels(): void {
    this.broker = this.factory.createMessageBroker(CHANNEL_RATE_SUBSCRIPTION, true, true);

    this.broker.registerMethod(
      "getActiveSubscriptions",
      [],
      this.getActiveSubscriptions.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "hasSubscription",
      [PRIMITIVE],
      this.hasSubscription.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod("subscribe", [PRIMITIVE], this.subscribe.bind(this), this.VALUE);
    this.broker.registerMethod("unsubscribe", [PRIMITIVE], this.unsubscribe.bind(this), PRIMITIVE);
    this.broker.registerMethod(
      "unsubscribeWithCheck",
      [PRIMITIVE],
      this.unsubscribeWithCheck.bind(this),
      PRIMITIVE,
    );

    this.rateSubscriptionSubject.subscribe((subscriptionEvent: RateSubscriptionEvent) => {
      if (
        subscriptionEvent.type == RateSubscriptionEventType.SUBSCRIBE &&
        !this.isSubscribed(subscriptionEvent.rateSubscription)
      ) {
        this.subscribe(subscriptionEvent.rateSubscription);
      } else if (
        subscriptionEvent.type == RateSubscriptionEventType.UNSUBSCRIBE &&
        !this.pendingUnsubscribe.key[subscriptionEvent.rateSubscription.key]
      ) {
        this.unsubscribe(subscriptionEvent.rateSubscription);
      }
    });
  }

  public registerRateSubscriptionSource(source: IRateSubscriptionSource): void {
    source.registerRateSubscriptionSourceSubject(this.rateSubscriptionSubject);
  }

  public hasSubscription(rateSubscription: RateSubscription): Promise<boolean> {
    return new Promise<boolean>((res) => {
      res(this.isSubscribed(rateSubscription));
    });
  }

  public isSubscribed(rateSubscription: RateSubscription): boolean {
    //let key: string = rateSubscription.key;
    let subscription = new RateSubscription(
      rateSubscription.assetCode,
      rateSubscription.product,
      rateSubscription.tenor,
      rateSubscription.valueDate,
    );
    let key: string = subscription.key;
    return this.subscriptions[key] != null || this.pendingSubscriptions[key] != null;
  }

  public getActiveSubscriptions(): Promise<Array<RateSubscription>> {
    return new Promise<Array<RateSubscription>>((res) => {
      //res(this.subscriptions.slice(0)); // old
      res(Object.keys(this.subscriptions).map((key) => this.subscriptions[key]));
    });
  }

  public subscribeWithChecks(
    rateSubscriptions: Array<RateSubscription>,
  ): Promise<Array<RateSubscription>> {
    return new Promise<Array<any>>((resolve, reject) => {
      Promise.all(rateSubscriptions.map((a) => this.hasSubscription(a))).then(
        (subscribed: boolean[]) => {
          let unsubscribed: RateSubscription[] = rateSubscriptions.filter(
            (subscription: RateSubscription, index: number) => subscribed[index] === false,
          );

          if (unsubscribed.length > 0) {
            let request: CubiqTS.BulkMarketDataRequestImpl =
              new CubiqTS.BulkMarketDataRequestImpl();
            request.marketDataRequests = unsubscribed.map((o) =>
              this.createMarketDataRequest(o, CubiqTS.ActionType.SUBSCRIBE),
            );

            this.worker
              .postServiceRequest(request)
              .then((value: CubiqTS.StandardReportImpl) => {
                resolve(unsubscribed);
              })
              .catch((reason) => reject(reason));
          } else {
            resolve([]);
          }
        },
      );
    });
  }

  public unsubscribeWithChecks(
    rateSubscriptions: Array<RateSubscription>,
  ): Promise<Array<CubiqTS.MarketDataEventImpl>> {
    return new Promise<Array<any>>((resolve, reject) => {
      Promise.all(rateSubscriptions.map((a) => this.hasSubscription(a))).then(
        (subscribed: boolean[]) => {
          let subscriptions: RateSubscription[] = rateSubscriptions.filter(
            (subscription: RateSubscription, index: number) => subscribed[index] === true,
          );
          Promise.all(subscriptions.map((s) => this.orderService.hasOrdersForRateKey(s.key))).then(
            (hasOrders: boolean[]) => {
              let orderless: RateSubscription[] = subscriptions.filter(
                (value: RateSubscription, index: number) => hasOrders[index] === false,
              );

              if (orderless.length > 0) {
                let request: CubiqTS.BulkMarketDataRequestImpl =
                  new CubiqTS.BulkMarketDataRequestImpl();
                request.marketDataRequests = orderless.map((o) =>
                  this.createMarketDataRequest(o, CubiqTS.ActionType.UNSUBSCRIBE),
                );
                this.worker
                  .postServiceRequest(request)
                  .then((value: CubiqTS.StandardReportImpl) => {
                    resolve(value.results);
                  })
                  .catch((reason) => reject(reason));
              } else {
                resolve([]);
              }
            },
          );
        },
      );
    });
  }

  public subscribe(rateSubscription: RateSubscription): Promise<any> {
    let subscription = new RateSubscription(
      rateSubscription.assetCode,
      rateSubscription.product,
      rateSubscription.tenor,
      rateSubscription.valueDate,
    );
    let key: string = subscription.key;
    return new Promise<any>((res, rej) => {
      let errors: CubiqTS.Errors = null;

      if (this.subscriptions[key]) {
        errors = this.produceError("Subscription for " + key + " already exists.");
      }

      if (this.pendingSubscriptions[key]) {
        errors = this.produceError("Subscription for " + key + " is pending.");
      }

      if (!errors) {
        logger.out.info("Subscribing for " + key);
        this.pendingSubscriptions[key] = subscription;
        this.sentSubscribeRequest(subscription)
          .then((event: CubiqTS.MarketDataEventImpl) => {
            delete this.pendingSubscriptions[key];
            this.subscriptions[key] = subscription;
            res(event);
            // Send the rate returned by subscription to the rate cache
            let listeners = this.worker
              .getBroker()
              .getEventListeners(CubiqTS.MarketDataEventImpl.name);
            for (let i = 0; i < listeners.length; i++) {
              try {
                listeners[i](event);
              } catch (ex) {
                logger.out.info(
                  "Error upon invoking event listener for rate subscription response for " + key,
                );
              }
            }
          })
          .catch((errors: CubiqTS.Errors) => {
            logger.out.warn("Error subscribing for " + key);
            delete this.pendingSubscriptions[key];
            rej(errors);
          });
      } else {
        logger.out.warn("Error subscribing for " + key);
        rej(errors);
      }
    });
  }

  public unsubscribeWithCheck(rateSubscription: RateSubscription): Promise<any> {
    let subscription = new RateSubscription(
      rateSubscription.assetCode,
      rateSubscription.product,
      rateSubscription.tenor,
      rateSubscription.valueDate,
    );
    return new Promise<any>((res, rej) => {
      this.orderService.hasOrdersForRateKey(subscription.key).then((outcome) => {
        if (outcome === false) {
          // no order data subscription
          res(this.unsubscribe(subscription));
        } else {
          res(false);
        }
      });
    });
  }

  public unsubscribe(subscription: RateSubscription): Promise<any> {
    let key: string = subscription.key;

    return new Promise<any>((res, rej) => {
      let errors: CubiqTS.Errors = null;

      if (this.pendingUnsubscribe[key]) {
        errors = this.produceError("Subscription " + key + " already pending unsubscribe.");
      }

      if (!this.subscriptions[key]) {
        errors = this.produceError("Subscription " + key + " does not exist.");
      }

      if (!errors) {
        logger.out.info("Unsubscribing " + key);
        delete this.subscriptions[key];
        this.pendingUnsubscribe[key] = subscription;

        this.sentSubscribeRequest(subscription, CubiqTS.ActionType.UNSUBSCRIBE)
          .then((event: CubiqTS.MarketDataEventImpl) => {
            delete this.pendingUnsubscribe[key];
            res(event);
          })
          .catch((errors: CubiqTS.Errors) => {
            delete this.pendingUnsubscribe[key];
            rej(errors);
          });
      } else {
        rej(errors);
      }
    });
  }

  protected createMarketDataRequest(
    subscription: RateSubscription,
    action: CubiqTS.ActionType,
  ): CubiqTS.MarketDataRequestImpl {
    let request: CubiqTS.MarketDataRequestImpl = new CubiqTS.MarketDataRequestImpl();

    request.actionType = action;
    request.subscriptionCode = subscription.assetCode;
    request.subscriptionClass = subscription.product;
    request.tenor = subscription.tenor;

    if (subscription.valueDate != null) {
      let valueDate: CubiqTS.StandardDateImpl = new CubiqTS.StandardDateImpl();
      valueDate.day = subscription.valueDate.getUTCDate();
      valueDate.month = subscription.valueDate.getUTCMonth() + 1;
      valueDate.year = subscription.valueDate.getUTCFullYear();
      request.valueDate = valueDate;

      if (CubiqTS.Tenor.EMPTY !== subscription.tenor && subscription.tenor !== null) {
        logger.out.warn(
          `${this.constructor.name}.sentSubscribeRequest() dropping ` +
            `Tenor.${CubiqTS.Tenor[subscription.tenor]} from request for valueDate based request.`,
        );
      }

      request.tenor = CubiqTS.Tenor.EMPTY;
    }

    return request;
  }

  protected sentSubscribeRequest(
    subscription: RateSubscription,
    action: CubiqTS.ActionType = CubiqTS.ActionType.SUBSCRIBE,
  ): Promise<CubiqTS.MarketDataEventImpl> {
    let request: CubiqTS.MarketDataRequestImpl = this.createMarketDataRequest(subscription, action);

    return new Promise<CubiqTS.MarketDataEventImpl>((res, rej) => {
      this.worker.postServiceRequest(request).then((value: any) => {
        if (value && value.enType && value.enType == ENCODABLE_TYPE_ERRORS) {
          rej(value);
        } else {
          res(value);
        }
      });
    });
  }

  protected produceError(...messages: string[]): CubiqTS.Errors {
    let errors: CubiqTS.ErrorsImpl = new CubiqTS.ErrorsImpl();

    errors.errorMessages = [];

    for (let i: number = 0; i < messages.length; i++) {
      let error: any = { id: messages[i] };
      let message: CubiqTS.Message = new CubiqTS.Message(JSON.stringify(error));
      errors.errorMessages.push(message);
    }

    return errors;
  }
}
